import {
  NavigationButtonProps,
  NavigationButtons,
  TextArea,
} from '@molecules/index';
import { MapComponent } from '@organisms/index';
import { addressResponseType, isWithinTfLBoundary } from '@utils';
import 'leaflet/dist/leaflet.css';
import { ErrorSummary, InlineErrorMessage, Label } from '@atoms/index';
import { AssetTypeConfig, ErrorMessages } from '@types';
import { LatLngTuple } from 'leaflet';
import { useState } from 'react';

export interface AdditionalLocationDetailsTextAreaProps {
  id: string;
  name: string;
  labelText: string;
  isLabelTextTitle?: boolean;
  value: string;
  description?: string;
  errorPath?: string;
  errorMessages?: ErrorMessages[];
  onChange: (event: React.ChangeEvent<HTMLTextAreaElement>) => void;
  maxLength?: number;
  showCounter?: boolean;
}

export interface MapTemplateProps {
  id?: string;
  labelText: string;
  subText?: string | React.ReactNode;
  errorPath?: string;
  errorMessages: ErrorMessages[];
  onChangeAddress: (address: addressResponseType) => void;
  selectedAddress: addressResponseType;
  customAddressContent?: string | React.ReactNode;
  additionalLocationDetailsTextArea?: AdditionalLocationDetailsTextAreaProps;
  assetType?: AssetTypeConfig | AssetTypeConfig[] | undefined;
  buttonNext?: NavigationButtonProps | undefined;
  buttonPrevious?: NavigationButtonProps | undefined;
  defaultLocation?: LatLngTuple;
  enableBuffer?: boolean;
  enableTfLBoundaryCheck?: boolean;
  navigationIfInsideTfLBoundary?: () => void;
  displayMapAssets?: Boolean;
}

export const MapTemplate = ({
  id,
  labelText,
  errorPath,
  errorMessages,
  onChangeAddress,
  selectedAddress,
  customAddressContent,
  additionalLocationDetailsTextArea,
  assetType,
  buttonNext,
  buttonPrevious,
  subText = null,
  defaultLocation,
  enableBuffer,
  enableTfLBoundaryCheck,
  navigationIfInsideTfLBoundary,
  displayMapAssets = false,
}: MapTemplateProps) => {
  const [loading, setLoading] = useState(false);
  const defaultButtonNext = {
    ...buttonNext,
    loading: loading || buttonNext?.loading,
    onClick: async (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      setLoading(true);
      if (
        enableTfLBoundaryCheck &&
        assetType &&
        selectedAddress.latitude &&
        selectedAddress.longitude
      ) {
        const isInsideTfLBoundary = await isWithinTfLBoundary(
          selectedAddress,
          'HighwaysCarriageways',
        );
        if (isInsideTfLBoundary) {
          if (navigationIfInsideTfLBoundary) {
            navigationIfInsideTfLBoundary();
          }
          setLoading(false);
          return;
        }
      }
      if (
        buttonNext &&
        'onClick' in buttonNext &&
        typeof buttonNext.onClick === 'function'
      ) {
        buttonNext.onClick(e);
      }
      setLoading(false);
    },
  } as NavigationButtonProps;

  return (
    <div className="lg:max-w-[80vw]">
      <ErrorSummary messages={errorMessages} />
      <div className="mt-4 flex flex-col gap-y-6">
        <Label
          className="!text-brand-primary !text-h2"
          htmlFor={errorPath ?? id}
          id={errorPath ?? id}
          text={labelText}
          isTitle={true}
        />
        {typeof subText === 'string' ? (
          <p className="text-bodyLg my-2">{subText}</p>
        ) : (
          subText
        )}
        <InlineErrorMessage path={errorPath} messages={errorMessages} />
        <MapComponent
          onChange={onChangeAddress}
          defaultLocation={defaultLocation}
          selectedAddress={selectedAddress}
          assetType={assetType}
          displayMapAssets={!!displayMapAssets}
          customAddressContent={customAddressContent}
          enableBuffer={enableBuffer}
          enableTfLBoundaryCheck={enableTfLBoundaryCheck}
        />
        {additionalLocationDetailsTextArea ? (
          <TextArea
            id={additionalLocationDetailsTextArea.id}
            name={additionalLocationDetailsTextArea.name}
            labelText={additionalLocationDetailsTextArea.labelText}
            description={additionalLocationDetailsTextArea.description}
            showCounter={additionalLocationDetailsTextArea.showCounter}
            isLabelTextTitle={
              additionalLocationDetailsTextArea.isLabelTextTitle
            }
            value={additionalLocationDetailsTextArea.value}
            onChange={additionalLocationDetailsTextArea.onChange}
            maxLength={additionalLocationDetailsTextArea.maxLength}
            errorPath={additionalLocationDetailsTextArea.errorPath}
            errorMessages={additionalLocationDetailsTextArea.errorMessages}
          />
        ) : null}
        {buttonPrevious && buttonNext ? (
          <NavigationButtons
            buttonPrevious={buttonPrevious}
            buttonNext={defaultButtonNext}
          />
        ) : null}
      </div>
    </div>
  );
};
